import { useApolloClient } from "@apollo/client";
import { CheckCircleOutlined as CheckCircleOutlineIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  GetAddressProcess,
  GetAddressProcessRef,
} from "../../addresses/GetAddressProcess.js";
import { useCrmCompanyDetailsForm_ModifyCrmCompanyMutation } from "../../crm-companies/boxes/CrmCompanyDetailsBox.generated.js";
import {
  PurchaseOrderMarkAsOrderedButton_OrderFragment,
  useSubmitOrderMutation,
} from "./PurchaseOrderMarkAsOrderedButton.generated.js";

interface Props {
  orderId: string;
  order: PurchaseOrderMarkAsOrderedButton_OrderFragment;
}

export const PurchaseOrderMarkAsOrderedButton = ({ orderId, order }: Props) => {
  const { t } = useTranslate([
    "PurchaseOrders",
    "Global",
    "OrganisationProfile",
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const getAddressProcessRef = React.useRef<GetAddressProcessRef>(null);

  const client = useApolloClient();

  const [submitOrder, { loading: submitOrderLoading }] = useSubmitOrderMutation(
    { client }
  );
  const [modifyCrmOrganisation, { loading: modifyCrmOrganisationLoading }] =
    useCrmCompanyDetailsForm_ModifyCrmCompanyMutation({
      client,
    });

  const handleSubmitOrder = async () => {
    try {
      if (
        order?.crmSupplier &&
        !order.crmSupplier.branchAddress &&
        !order.crmSupplier.billingAddress
      ) {
        const address = await getAddressProcessRef.current?.getAddress({
          title: order.crmSupplier.title,
          inputLabel: t("Branch address", { ns: "OrganisationProfile" }),
          description: t("Please provide a branch address for supplier", {
            ns: "PurchaseOrders",
          }),
        });
        if (!address) return;
        await modifyCrmOrganisation({
          variables: {
            crmCompanies: [
              {
                id: order.crmSupplier.id,
                branchAddress: {
                  ...omit(address, "__typename", "id"),
                  location: address.location
                    ? omit(address.location, "__typename")
                    : null,
                },
              },
            ],
          },
        });
      }

      await submitOrder({ variables: { orderId } });

      enqueueSnackbar(
        t("Document marked as ordered", { ns: "PurchaseOrders" })
      );
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar(e.message, { variant: "error" });
      }
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        endIcon={<CheckCircleOutlineIcon />}
        loading={modifyCrmOrganisationLoading || submitOrderLoading}
        onClick={handleSubmitOrder}
        disabled={order.items.length === 0}
      >
        {t("Mark as ordered", { ns: "PurchaseOrders" })}
      </Button>
      <GetAddressProcess ref={getAddressProcessRef} />
    </>
  );
};
